/**
 * YouTube embed helpers for Auto-book explainer (T2-6434).
 * Native apps use an HTTPS proxy page so YouTube receives a valid Referer.
 */

import config from 'api/config';
import { isNativeApp } from './nativeApp';

/** Develop web host that serves `/embed/youtube.html` (deploy target for iOS proxy QA). */
export const INSTABOOK_EMBED_PROXY_DEVELOP_URL = 'https://app.dev.tempstars.com';

export function isDevelopApiBaseUrl(baseUrl: string): boolean {
  const normalized = (baseUrl || '').toLowerCase();
  return (
    normalized.includes('localhost')
    || normalized.includes('127.0.0.1')
    || /^https?:\/\/10\./.test(normalized)
    || /^https?:\/\/192\.168\./.test(normalized)
    || /^https?:\/\/172\.(1[6-9]|2\d|3[01])\./.test(normalized)
    || normalized.includes('api.dev.tempstars')
    || normalized.includes('api.beta.tempstars')
  );
}

export function extractYoutubeVideoId(url: string): string | null {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;
  const embed = trimmed.match(/youtube\.com\/embed\/([^?&]+)/i);
  if (embed) return embed[1];
  const watch = trimmed.match(/[?&]v=([^?&]+)/i);
  if (watch) return watch[1];
  const short = trimmed.match(/youtu\.be\/([^?&]+)/i);
  if (short) return short[1];
  return null;
}

/** HTTPS host that serves /embed/youtube.html. */
export function getInstabookEmbedProxyBaseUrl(): string {
  if (isDevelopApiBaseUrl(config?.server?.baseUrl || '')) {
    return INSTABOOK_EMBED_PROXY_DEVELOP_URL;
  }
  return (config?.web?.appUrl || 'https://app.tempstars.com').replace(/\/$/, '');
}

/** Proxy page URL — inner page embeds YouTube with Referer = proxy origin. */
export function buildInstabookProxyEmbedUrl(rawUrl: string, baseUrl = getInstabookEmbedProxyBaseUrl()): string | null {
  const videoId = extractYoutubeVideoId(rawUrl);
  if (!videoId) return null;
  return `${baseUrl}/embed/youtube.html?v=${encodeURIComponent(videoId)}`;
}

/**
 * iframe src for Auto-book video:
 * - Cordova (iOS/Android): HTTPS proxy on develop or production app host
 * - Web browser: direct API embed URL (works in normal browsers)
 */
export function buildInstabookVideoIframeSrc(apiEmbedUrl: string): string {
  if (isNativeApp()) {
    return buildInstabookProxyEmbedUrl(apiEmbedUrl) || apiEmbedUrl;
  }
  return apiEmbedUrl;
}

export function toWatchableVideoUrl(url: string): string {
  const videoId = extractYoutubeVideoId(url);
  if (videoId) return `https://www.youtube.com/watch?v=${videoId}`;
  return url;
}

/** Fallback if proxy iframe still fails on device. */
export function openInstabookVideo(rawUrl: string): void {
  const watchUrl = toWatchableVideoUrl(rawUrl);
  const w = window as any;
  if (w.cordova?.InAppBrowser) {
    w.cordova.InAppBrowser.open(watchUrl, '_system');
    return;
  }
  window.open(watchUrl, '_blank', 'noopener,noreferrer');
}
